// ImportMethodSheet — spec §1a. Single job: present the two-path import entry
// (Screenshot vs SlipSync + book row) and hand the chosen path back to App via
// onChooseScreenshot / the SlipSync hook. Reads: dataSource, user, and the user's access via
// /api/beta-status (useBetaStatus, re-checked on each open; #1503 invite-only). Writes:
// nothing (SlipSync's own hook owns the sync state machine). Does NOT own the
// vision import flow or App's importSurface — App maps onChooseScreenshot →
// "screenshot" and closes on completion.
//
// Behavior contract is §12a (authoritative over the prose "closes the sheet"):
// while syncStatus is OPENING_WEBVIEW/SUBMITTING the sheet stays mounted and
// `locked` (backdrop/swipe close disabled). Only books with a shipped SlipSync config are
// listed (no disabled placeholders); FD runs at lifecycle stage 'capture' (#885): its chip starts
// a capture that imports nothing, and the capture note says so before the tap.
import { useState, useRef, useEffect } from "react";
import BottomSheet from "./BottomSheet";
import { JT } from "./ui/jumbotronTokens";
import { useSlipSync } from "../hooks/useSlipSync";
import { useBetaStatus } from "../hooks/useBetaStatus";
import { accessDeniedMessage } from "../lib/accessCopy";
import StateSetupModal from "../features/slipsync/StateSetupModal";
import SlipSyncConsentSheet from "../features/slipsync/SlipSyncConsentSheet";
import { SyncProgressPanel, SyncResultPanel } from "./ui/SyncFeedback";
import { SYNC_STATUS } from "../lib/slipsync/constants";
import { dkConfig } from "../lib/slipsync/books/dk";
import { fdConfig } from "../lib/slipsync/books/fd";
import { mgmConfig } from "../lib/slipsync/books/mgm";

// Book selector order per §1a: DraftKings · FanDuel · BetMGM. Only books with a shipped SlipSync config are
// listed: disabled "soon" placeholders (William Hill) read as unfinished features in App Review (2.1), so they
// return when their configs ship.
const BOOKS = [
  { key: "dk", label: dkConfig.displayName, config: dkConfig, enabled: true },
  { key: "fd", label: fdConfig.displayName, config: fdConfig, enabled: true },
  { key: "mgm", label: mgmConfig.displayName, config: mgmConfig, enabled: true },
];
const CAPTURE_BOOKS = BOOKS.filter((b) => b.enabled && b.config && b.config.stage === "capture");

const ScreenshotGlyph = (
  <svg width="18" height="18" viewBox="0 0 22 22" fill="none">
    <rect x="3" y="6" width="16" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 6 L9.2 4 H12.8 L14 6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="11" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
const SyncGlyph = (
  <svg width="18" height="18" viewBox="0 0 22 22" fill="none">
    <path d="M5 8 A6 6 0 0 1 16.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M17 8 V4.5 H13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 14 A6 6 0 0 1 5.5 15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M5 14 V17.5 H8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function IconCircle({ children, loading }) {
  return (
    <div style={{
      width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
      background: JT.bg.well, color: JT.brand.accent,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {loading
        ? <div className="skeleton" style={{ width: 22, height: 22, borderRadius: "50%" }} />
        : children}
    </div>
  );
}

function OptionRow({ onClick, disabled, icon, loading, label, description }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "center", gap: 14, width: "100%",
        background: JT.bg.tile, border: `1px solid ${JT.border.mid}`, borderRadius: 12,
        padding: 16, cursor: disabled ? "default" : "pointer", textAlign: "left",
        fontFamily: "inherit", opacity: disabled ? 0.4 : 1,
      }}
    >
      <IconCircle loading={loading}>{icon}</IconCircle>
      <div style={{ minWidth: 0 }}>
        <div style={{ ...JT.type.label, fontWeight: 800, letterSpacing: "1.5px", color: JT.text.primary }}>{label}</div>
        <div style={{ fontSize: 11, fontWeight: 500, color: JT.text.secondary, marginTop: 3 }}>{description}</div>
      </div>
    </button>
  );
}

export default function ImportMethodSheet({ open, onClose, dataSource, user, onSyncComplete, onChooseScreenshot, onSignIn }) {
  const [selectedBook, setSelectedBook] = useState(null);
  const [showBooks, setShowBooks] = useState(false);
  const pendingSyncRef = useRef(false);

  // #1226: set on the transition into SUCCESS while open, cleared on dismiss, so the
  // result shows once per sync and a reopened sheet starts at the entry options.
  const [showResult, setShowResult] = useState(false);

  const {
    syncStatus, triggerSync, isIos, showStateSetup, saveStateConfig, errorMessage, needsReauth, resultSummary, foundCount,
    showConsent, consentBusy, consentError, acceptConsent, declineConsent, syncNotice, reconnectInfo, canReconnect, reconnect,
  } = useSlipSync({
    user,
    onSyncComplete,
    book: selectedBook || mgmConfig,
  });

  // #1503 invite-only: an account the server has not approved never reaches a sportsbook sign-in. The check runs
  // on each open (auto:false + refetch below), so an approval or a revoke shows on the next open (pre-Codex FC-2/3).
  // While it loads (allowed === null) the books stay usable: /api/slipsync refuses an unapproved sync anyway.
  const access = useBetaStatus({ user, auto: false });
  const accessDenied = Boolean(user) && access.allowed === false;
  const refetchAccess = access.refetch;
  useEffect(() => {
    if (open && user) refetchAccess();
  }, [open, user, refetchAccess]);
  // A denial that lands while the consent prompt is up drops it, so "I agree" cannot resume a sync (#1503 delta FC-3).
  useEffect(() => {
    if (accessDenied && showConsent) declineConsent();
  }, [accessDenied, showConsent, declineConsent]);

  const isSyncing = syncStatus === SYNC_STATUS.OPENING_WEBVIEW || syncStatus === SYNC_STATUS.SUBMITTING;
  const prevStatusRef = useRef(syncStatus);
  const screenshotLoading = dataSource === "loading";

  // Fire the sync for the just-selected book once useSlipSync has rebound its
  // triggerSync to that book (triggerSync identity changes with `book`).
  useEffect(() => {
    if (pendingSyncRef.current && selectedBook) {
      pendingSyncRef.current = false;
      triggerSync();
    }
  }, [selectedBook, triggerSync]);

  // Completion shows the sync result in the sheet (#1226; it used to close silently, so
  // users could not tell the sync worked). Only on the transition INTO SUCCESS -- never
  // when the sheet is reopened while syncStatus is still SUCCESS from a prior sync, which
  // would replay a stale result (and, when this closed the sheet, bricked import: Codex P1).
  useEffect(() => {
    if (open && syncStatus === SYNC_STATUS.SUCCESS && prevStatusRef.current !== SYNC_STATUS.SUCCESS) {
      setShowResult(true);
    }
    prevStatusRef.current = syncStatus;
  }, [open, syncStatus, onClose]);

  // Reset the inline book row AND the selected-book binding whenever the sheet is
  // dismissed, so each open starts from a clean book state (prevents a stale
  // selectedBook from binding useSlipSync to the wrong book on the next open).
  useEffect(() => {
    if (!open) {
      setShowBooks(false);
      setSelectedBook(null);
      setShowResult(false);
      // #1415 Review Focus 4: a closed sheet drops the consent prompt, so a later "I agree" never resumes a sync.
      declineConsent();
    }
  }, [open, declineConsent]);

  const pickBook = (book) => {
    if (!book.enabled || isSyncing || accessDenied) return;
    // Re-tapping the already-selected book (e.g. retry after an ERROR) assigns the same
    // config ref, so React skips the state update and the [selectedBook] effect won't
    // rerun — trigger directly in that case (Codex P1). Selecting a *new* book goes
    // through the effect once useSlipSync has rebound triggerSync to it.
    if (selectedBook === book.config) {
      triggerSync();
    } else {
      setSelectedBook(book.config);
      pendingSyncRef.current = true;
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} locked={isSyncing || consentBusy}>
      {syncStatus === SYNC_STATUS.SUBMITTING ? (
        <div style={{ padding: 20 }}>
          <SyncProgressPanel book={selectedBook} foundCount={foundCount} capture={selectedBook?.stage === "capture"} />
        </div>
      ) : showResult && syncStatus === SYNC_STATUS.SUCCESS ? (
        <div style={{ padding: 20 }}>
          <SyncResultPanel book={selectedBook} summary={resultSummary} onDone={onClose} onAddScreenshot={() => onChooseScreenshot()} />
        </div>
      ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 20 }}>
        <OptionRow
          onClick={() => onChooseScreenshot()}
          disabled={screenshotLoading || isSyncing}
          loading={screenshotLoading}
          icon={ScreenshotGlyph}
          label="Screenshot"
          description="Import a screenshot from your photo library"
        />

        <OptionRow
          onClick={() => setShowBooks((s) => !s)}
          disabled={!isIos || isSyncing}
          icon={SyncGlyph}
          label="SlipSync"
          description={isIos
            ? "Sync your bet history from DraftKings or BetMGM"
            : "SlipSync is currently available in the iOS app"}
        />

        {/* SlipSync writes bets under the user's account, so it requires auth. A
            signed-out iOS guest would otherwise reach triggerSync(), which the hook
            silently no-ops when user is null (Codex P1). Surface sign-in first. */}
        {showBooks && isIos && !user && (
          <div style={{ marginTop: 4, background: JT.bg.tile, border: `1px solid ${JT.border.mid}`, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: JT.text.secondary, marginBottom: 10 }}>
              Sign in to sync your bets from a sportsbook.
            </div>
            <button
              onClick={() => onSignIn && onSignIn()}
              style={{
                padding: "8px 16px", borderRadius: 10, fontFamily: "inherit", fontSize: 12, fontWeight: 800,
                letterSpacing: "0.5px", cursor: "pointer", color: JT.text.primary,
                background: "linear-gradient(135deg,#1d4ed8,#4f46e5)", border: "none",
              }}
            >
              Sign in
            </button>
          </div>
        )}

        {showBooks && isIos && user && accessDenied && (
          <div data-testid="slipsync-access-denied" style={{ marginTop: 4, background: JT.bg.tile, border: `1px solid ${JT.border.mid}`, borderRadius: 12, padding: 16, fontSize: 12, color: JT.text.secondary, lineHeight: 1.45 }}>
            {accessDeniedMessage(access.status)}
          </div>
        )}

        {showBooks && isIos && user && !accessDenied && (
          <div data-testid="slipsync-books" style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
            {BOOKS.map((book) => {
              // #1415 Codex r4: no book switch while the disclosure is up or saving.
              const disabled = !book.enabled || isSyncing || showConsent || consentBusy;
              return (
                <button
                  key={book.key}
                  onClick={disabled ? undefined : () => pickBook(book)}
                  disabled={disabled}
                  style={{
                    padding: "8px 14px", borderRadius: 10, fontFamily: "inherit",
                    fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer",
                    background: JT.bg.tile,
                    border: `1px solid ${JT.border.mid}`,
                    color: book.enabled ? JT.brand.accent : JT.text.tertiary,
                    opacity: book.enabled ? 1 : 0.4,
                  }}
                >
                  {book.label}
                </button>
              );
            })}
          </div>
        )}

        {/* #885: a capture-stage book imports nothing yet; say so before the user taps it. */}
        {showBooks && isIos && user && !accessDenied && CAPTURE_BOOKS.length > 0 && (
          <div data-testid="slipsync-capture-note" style={{ fontSize: 12, color: JT.text.secondary, lineHeight: 1.45 }}>
            {CAPTURE_BOOKS.map((b) => b.config.captureCopy).join(" ")}
          </div>
        )}

        {/* Bridge hardening (spec §7): say the sync is starting while the window opens. */}
        {syncNotice && syncStatus === SYNC_STATUS.OPENING_WEBVIEW && (
          <div role="status" style={{ fontSize: 12, color: JT.text.secondary, lineHeight: 1.45 }}>{syncNotice}</div>
        )}

        {/* First-run region gate (spec §1a): a book with needsRegionSelection and no
            saved region (e.g. BetMGM) surfaces this before the sync can proceed. The
            hook auto-resumes the pending sync once saveStateConfig sets the region. */}
        {showConsent && !accessDenied && <SlipSyncConsentSheet onAgree={acceptConsent} onDecline={declineConsent} busy={consentBusy} error={consentError} />}
        {showStateSetup && !accessDenied && <StateSetupModal onSave={saveStateConfig} />}

        {/* Terminal SlipSync failure (cancel/timeout/server/network/auth) — surface it
            instead of silently re-enabling the book row (Codex P1). */}
        {/* #1509: when the sheet already shows its denied panel, the hook's denial (same copy) is not repeated. */}
        {syncStatus === SYNC_STATUS.ERROR && errorMessage && !accessDenied && (
          <div style={{
            fontSize: 12, fontWeight: 600, color: JT.status.lost.color,
            background: JT.status.lost.dim, border: `1px solid ${JT.status.lost.border}`,
            borderRadius: 10, padding: "10px 12px",
          }}>
            {errorMessage}{needsReauth ? " Please reconnect your sportsbook and try again." : ""}
          </div>
        )}

        {/* Bridge hardening (spec §7, P1-4): Reconnect clears the failure ladder for the refused book only. */}
        {reconnectInfo && !accessDenied && (
          <button type="button" onClick={reconnect} disabled={!canReconnect}
            style={{ alignSelf: "flex-start", minHeight: 44, padding: "10px 16px", borderRadius: 8, border: `1px solid ${JT.border.mid}`, background: "none",
              color: canReconnect ? JT.text.primary : JT.text.tertiary, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: canReconnect ? "pointer" : "default" }}>
            Reconnect
          </button>
        )}
      </div>
      )}
    </BottomSheet>
  );
}
